// ─────────────────────────────────────────────────────────────────────────────
// What a stored file looks like once it is in the form.
//
// A filename on its own is not a preview: the whole point of picking a header
// image is seeing that you picked the right one.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { kindOf, type MediaValue } from './types.js';

export interface MediaThumbProps {
  value: MediaValue;
  /** Square edge in px. Default 56. */
  size?: number;
}

/** A thumbnail for any MIME: the picture itself where there is one, an icon otherwise. */
export function MediaThumb({ value, size = 56 }: MediaThumbProps) {
  const [broken, setBroken] = useState(false);
  const kind = kindOf(value.mime_type);
  const style = { width: size, height: size };

  // A signed URL that has expired 403s, and a broken <img> is a worse answer
  // than the icon for its kind.
  if (kind === 'image' && value.url && !broken) {
    return (
      <img
        className="rr-media-thumb rr-media-thumb--image"
        style={style}
        src={value.url}
        alt={value.original_name ?? ''}
        loading="lazy"
        onError={() => setBroken(true)}
      />
    );
  }

  if (kind === 'video' && value.url && !broken) {
    return (
      <video
        className="rr-media-thumb rr-media-thumb--image"
        style={style}
        src={value.url}
        muted
        playsInline
        preload="metadata"
        onError={() => setBroken(true)}
      />
    );
  }

  return (
    <div className={`rr-media-thumb rr-media-thumb--${kind}`} style={style} aria-hidden>
      <KindIcon kind={kind} />
    </div>
  );
}

function KindIcon({ kind }: { kind: ReturnType<typeof kindOf> }) {
  const common = {
    width: 22, height: 22, viewBox: '0 0 20 20', fill: 'none',
    stroke: 'currentColor', strokeWidth: 1.5,
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const
  };
  if (kind === 'image') {
    return (
      <svg {...common}><rect x="2.5" y="3.5" width="15" height="13" rx="2" /><circle cx="7.2" cy="8" r="1.4" /><path d="M3 14l4.2-4 3 2.6 3-2.4 3.8 3.6" /></svg>
    );
  }
  if (kind === 'video') {
    return <svg {...common}><rect x="2.5" y="4.5" width="15" height="11" rx="2" /><path d="M8.4 8.2l4 1.8-4 1.8z" /></svg>;
  }
  if (kind === 'audio') {
    return <svg {...common}><path d="M7.5 12.5V4.8l7-1.3v7.7" /><circle cx="5.8" cy="13.3" r="1.8" /><circle cx="12.8" cy="11.9" r="1.8" /></svg>;
  }
  return <svg {...common}><path d="M5 2.4h7.4L16 6v11.6H5z" /><path d="M12.2 2.5v3.7h3.7" /><path d="M7.6 10.2h6M7.6 13.3h4" /></svg>;
}
